import { z } from "zod";
import { zId } from "./common.js";

/**
 * Segment query language: a tree of AND/OR groups over field conditions.
 * Fields address standard columns (`field: "lastName"`), giving aggregates
 * (`field: "giving.lifetime"`), and custom fields (`field: "custom.my_key"`).
 * CT-RM-34: custom object records linked to the constituent — `object.<type>`
 * (is_set / is_not_set: has any linked record), `object.<type>.name` and
 * `object.<type>.custom.<field>` (some linked record matches).
 */
export const SEGMENT_OPERATORS = [
  "eq",
  "neq",
  "contains",
  "not_contains",
  "starts_with",
  "gt",
  "gte",
  "lt",
  "lte",
  "is_set",
  "is_not_set",
  "in",
  "not_in",
  "before",
  "after",
  "within_last_days",
  "not_within_last_days",
  "anniversary_within_days",
] as const;
export const zSegmentOperator = z.enum(SEGMENT_OPERATORS);
export type SegmentOperator = z.infer<typeof zSegmentOperator>;

export const zSegmentCondition = z.object({
  kind: z.literal("condition"),
  field: z.string().min(1).max(120),
  operator: zSegmentOperator,
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.array(z.number())]).nullable(),
});
export type SegmentCondition = z.infer<typeof zSegmentCondition>;

export type SegmentGroup = {
  kind: "group";
  logic: "and" | "or";
  children: (SegmentCondition | SegmentGroup)[];
};

export const zSegmentGroup: z.ZodType<SegmentGroup> = z.lazy(() =>
  z.object({
    kind: z.literal("group"),
    logic: z.enum(["and", "or"]),
    children: z.array(z.union([zSegmentCondition, zSegmentGroup])).min(1).max(50),
  }),
);

export const zSegmentDefinition = z.object({
  root: zSegmentGroup,
});
export type SegmentDefinition = z.infer<typeof zSegmentDefinition>;

export const zSegment = z.object({
  id: zId,
  name: z.string(),
  description: z.string().nullable(),
  definition: zSegmentDefinition,
  isShared: z.boolean(),
  createdById: zId,
  memberCount: z.number().int().nullable(),
  memberCountAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Segment = z.infer<typeof zSegment>;

export const zUpsertSegment = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).optional(),
  definition: zSegmentDefinition,
  isShared: z.boolean().default(true),
});

/** Fields the segment engine can filter on, exposed so the builder UI and AI stay in sync. */
/** "link": existence of linked custom object records (is_set / is_not_set only) */
export type SegmentFieldType = "text" | "number" | "money" | "date" | "boolean" | "enum" | "link";
export interface SegmentFieldDef {
  field: string;
  label: string;
  type: SegmentFieldType;
  options?: string[];
  /** builder grouping, e.g. a custom object type's plural name */
  group?: string;
}

export const SEGMENT_FIELD_CATALOG: SegmentFieldDef[] = [
  { field: "type", label: "Constituent type", type: "enum", options: ["individual", "household", "organization", "foundation"] },
  { field: "displayName", label: "Name", type: "text" },
  { field: "firstName", label: "First name", type: "text" },
  { field: "lastName", label: "Last name", type: "text" },
  { field: "email", label: "Any email", type: "text" },
  { field: "phone", label: "Any phone", type: "text" },
  { field: "city", label: "City", type: "text" },
  { field: "state", label: "State", type: "text" },
  { field: "postalCode", label: "Postal code", type: "text" },
  { field: "tag", label: "Tag", type: "text" },
  { field: "deceased", label: "Deceased", type: "boolean" },
  { field: "createdAt", label: "Created date", type: "date" },
  { field: "giving.lifetime", label: "Lifetime giving", type: "money" },
  { field: "giving.ytd", label: "Giving this year", type: "money" },
  { field: "giving.largest", label: "Largest gift", type: "money" },
  { field: "giving.count", label: "Gift count", type: "number" },
  { field: "giving.firstGiftDate", label: "First gift date", type: "date" },
  { field: "giving.lastGiftDate", label: "Last gift date", type: "date" },
  { field: "score.rfm", label: "RFM score", type: "number" },
  { field: "score.lapseRisk", label: "Lapse risk (0–100)", type: "number" },
  { field: "score.upgradePropensity", label: "Upgrade propensity (0–100)", type: "number" },
  { field: "score.plannedGiftLikelihood", label: "Planned-gift likelihood (0–100)", type: "number" },
  { field: "score.engagement", label: "Engagement level (0–4)", type: "number" },
];
